import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';

export interface ModuleDef {
  key: string;
  icon: LucideIcon;
  color: string;
  route?: string;
}

interface ModuleGroupProps {
  groupLabel: string;
  modules: ModuleDef[];
}

export function ModuleGroup({ groupLabel, modules }: ModuleGroupProps) {
  const { t } = useTranslation();

  return (
    <section className="mt-10">
      <div className="flex items-center gap-3 mb-4">
        <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
          {t(`dashboard.groups.${groupLabel}`, groupLabel)}
        </h3>
        <div className="h-px flex-1 bg-border" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {modules.map((m) => {
          const Icon = m.icon;
          const isActive = !!m.route;
          const label = t(`dashboard.modules.${m.key}`, m.key);
          const status = isActive
            ? t('dashboard.status.open', 'Open')
            : t('dashboard.status.comingSoon', 'Coming soon');

          const card = (
            <div
              className={cn(
                'group relative rounded-xl border border-border bg-card p-5 transition-all',
                isActive
                  ? 'hover:shadow-md hover:border-border/80 cursor-pointer hover:-translate-y-0.5'
                  : 'opacity-60 border-dashed',
              )}
            >
              <div className={cn('inline-flex rounded-lg p-2 ring-1 ring-border/50 bg-background/50', m.color)}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="mt-3">
                <div className="font-medium text-base">{label}</div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-0.5">
                  {status}
                </div>
              </div>
              {isActive && (
                <span className="absolute top-4 end-4 h-1.5 w-1.5 rounded-full bg-emerald-500" />
              )}
            </div>
          );

          return m.route ? (
            <Link key={m.key} to={m.route} className="block">{card}</Link>
          ) : (
            <div key={m.key}>{card}</div>
          );
        })}
      </div>
    </section>
  );
}
